import { useCallback, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import {
  BarChart3,
  ExternalLink,
  Maximize2,
  LayoutDashboard,
  Lock,
  RefreshCw,
  Search,
  ShieldAlert,
  Users,
} from 'lucide-react';
import clsx from 'clsx';
import type { QueryResult, SavedQuery, SessionUser } from '@idas/shared';
import { api, ApiError } from '../lib/api';
import { pickForm } from '../lib/chart';
import { BarChart, KpiRow, LineChart, MiniTable, StatTile } from '../components/dashboard/Charts';
import { EmptyState, ListSkeleton } from '../components/ui/EmptyState';
import { InlineSpinner } from '../components/ui/Refreshing';
import { dashboardGate } from '../lib/query-gate';
import { useConsoleStore } from '../store/console.store';

/**
 * Dashboards.
 *
 * A saved query already is a dashboard tile — someone decided the question was
 * worth keeping and gave it a name. This page runs those queries and draws the
 * answers, so a number that took a Run-and-read every morning is just there.
 *
 * Nothing here bypasses anything: each tile is an ordinary query execution, so
 * the SQL guard, the row limits, column masking and the audit log all apply
 * exactly as they do in the console. A tile can therefore legitimately come
 * back denied, and says so rather than rendering an empty chart.
 */



export function DashboardsPage({ user }: { user: SessionUser }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [refreshToken, setRefreshToken] = useState(0);

  const saved = useQuery({ queryKey: ['saved-queries'], queryFn: api.savedQueries.list });
  const datasources = useQuery({ queryKey: ['datasources'], queryFn: api.datasources.list });

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const all = saved.data?.queries ?? [];
    if (!needle) return all;
    return all.filter(
      (q) =>
        q.name.toLowerCase().includes(needle) ||
        (q.description ?? '').toLowerCase().includes(needle),
    );
  }, [saved.data, search]);

  const refreshAll = useCallback(() => {
    setRefreshToken((n) => n + 1);
    void queryClient.invalidateQueries({ queryKey: ['tile'] });
  }, [queryClient]);

  const canRun = user.permissions.includes('query:run');

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-6xl p-4">
        <header className="mb-4 flex flex-wrap items-center gap-2">
          <LayoutDashboard className="h-4 w-4 text-ink-muted" />
          <h1 className="text-sm font-semibold text-ink">Dashboards</h1>
          <span className="text-2xs text-ink-muted">
            Built from your saved queries — each tile is a live run.
          </span>

          <div className="flex-1" />

          <div className="relative w-52">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-ink-faint" />
            <input
              className="input py-1 pl-7"
              placeholder="Search dashboards"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <button className="btn" onClick={refreshAll} disabled={!canRun}>
            <RefreshCw className="h-3 w-3" />
            Refresh all
          </button>
        </header>

        {!canRun && (
          <p className="mb-4 rounded-md border border-warn/30 bg-warn/5 p-3 text-2xs leading-relaxed text-warn">
            Your role cannot run queries, so tiles cannot be drawn. Auditors see the saved queries
            themselves in the console.
          </p>
        )}

        {saved.isLoading && <ListSkeleton rows={4} height={150} />}

        {!saved.isLoading && visible.length === 0 && (
          <EmptyState
            icon={BarChart3}
            title={search ? 'Nothing matches' : 'No dashboards yet'}
            body={
              search
                ? 'Try a different search.'
                : 'Dashboards are built from saved queries. Write a query in the console, save it with a name, and it appears here as a tile.'
            }
            action={
              !search ? (
                <button className="btn btn-primary" onClick={() => navigate('/console')}>
                  Go to the console
                </button>
              ) : undefined
            }
          />
        )}

        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {visible.map((query) => (
            <Tile
              key={query.id}
              query={query}
              enabled={canRun}
              refreshToken={refreshToken}
              connectionName={
                datasources.data?.datasources.find((d) => d.id === query.dataSourceId)?.name ?? null
              }
            />
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * One saved query, run and drawn.
 *
 * Given its own query key so a single tile can be refreshed without disturbing
 * the others, and so a failure is contained to the tile that caused it.
 */
function Tile({
  query,
  enabled,
  refreshToken,
  connectionName,
}: {
  query: SavedQuery;
  enabled: boolean;
  refreshToken: number;
  connectionName: string | null;
}) {
  const navigate = useNavigate();
  const clientQueryId = useRef(crypto.randomUUID());

  const run = useQuery({
    queryKey: ['tile', query.id, refreshToken],
    enabled,
    // These are live database reads, not cheap metadata: don't re-run them
    // because a window regained focus.
    refetchOnWindowFocus: false,
    staleTime: 5 * 60_000,
    // Retry only the one error that is transient by nature: the per-user
    // concurrency cap, hit when the analyst runs something in the console at
    // the same moment. Everything else — denied by policy, bad SQL — is a
    // fact about the query and retrying it just doubles the audit noise.
    retry: (attempt, error) =>
      attempt < 2 && error instanceof ApiError && error.code === 'QUERY_LIMIT_EXCEEDED',
    retryDelay: 1500,
    queryFn: () =>
      dashboardGate(() =>
        api.query.execute({
          dataSourceId: query.dataSourceId,
          sql: query.sql,
          clientQueryId: `${clientQueryId.current}-${refreshToken}`,
        }),
      ),
  });

  const openInConsole = () => {
    const store = useConsoleStore.getState();
    const tabId = store.addTab(query.sql);
    store.renameTab(tabId, query.name);
    navigate('/console');
  };

  return (
    <section className="panel flex h-64 flex-col rounded-lg">
      <header className="flex shrink-0 items-start gap-2 border-b border-edge px-3 py-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <Link
              to={`/dashboards/${query.id}`}
              className="truncate text-2xs font-semibold text-ink hover:text-accent hover:underline"
              title={query.name}
            >
              {query.name}
            </Link>
            {query.shared && (
              <span className="chip border-edge bg-surface-sunken text-ink-faint" title={`Shared by ${query.ownerName}`}>
                <Users className="h-2.5 w-2.5" />
                shared
              </span>
            )}
            {run.isFetching && <InlineSpinner />}
          </div>
          <p className="truncate text-2xs text-ink-faint" title={query.description ?? undefined}>
            {query.description || connectionName || ' '}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-0.5">
          <Link
            className="btn btn-ghost px-1.5"
            to={`/dashboards/${query.id}`}
            aria-label={`Open ${query.name} in full view`}
            title="Full view — filters, chart and every row"
          >
            <Maximize2 className="h-3 w-3" />
          </Link>
          <button
            className="btn btn-ghost px-1.5"
            onClick={() => void run.refetch()}
            disabled={!enabled || run.isFetching}
            aria-label={`Refresh ${query.name}`}
            title="Re-run this query"
          >
            <RefreshCw className={clsx('h-3 w-3', run.isFetching && 'animate-spin')} />
          </button>
          <button
            className="btn btn-ghost px-1.5"
            onClick={openInConsole}
            aria-label={`Open ${query.name} in the console`}
            title="Open in the console"
          >
            <ExternalLink className="h-3 w-3" />
          </button>
        </div>
      </header>

      <div className="min-h-0 flex-1 p-3" style={{ containerType: 'inline-size' }}>
        <TileBody result={run.data} error={run.error} loading={run.isLoading} enabled={enabled} />
      </div>

      {run.data && (
        <footer className="shrink-0 border-t border-edge px-3 py-1 text-2xs text-ink-faint">
          {run.data.rowCount.toLocaleString()} row{run.data.rowCount === 1 ? '' : 's'} ·{' '}
          {run.data.executionMs}ms
          {run.data.truncated && <span className="ml-1 text-warn">· truncated</span>}
        </footer>
      )}
    </section>
  );
}

function TileBody({
  result,
  error,
  loading,
  enabled,
}: {
  result: QueryResult | undefined;
  error: unknown;
  loading: boolean;
  enabled: boolean;
}) {
  if (!enabled) {
    return <Centered muted>Not available for your role.</Centered>;
  }

  if (loading) {
    return (
      <div className="flex h-full flex-col justify-end gap-2">
        {[70, 45, 85, 30].map((w, i) => (
          <div key={i} className="h-3 animate-pulse rounded-sm bg-surface-sunken" style={{ width: `${w}%` }} />
        ))}
      </div>
    );
  }

  if (error) {
    const message = error instanceof ApiError ? error.message : 'This query could not be run.';
    // A refused query is a policy outcome, not a broken tile — it gets the
    // lock, not an error triangle.
    const denied = error instanceof ApiError && error.code === 'POLICY_DENIED';
    return (
      <div className="flex h-full flex-col items-start justify-center gap-1.5">
        {denied ? (
          <Lock className="h-4 w-4 text-lock" />
        ) : (
          <ShieldAlert className="h-4 w-4 text-danger" />
        )}
        <p className={clsx('text-2xs leading-relaxed', denied ? 'text-lock' : 'text-danger')}>
          {message}
        </p>
      </div>
    );
  }

  if (!result) return <Centered muted>Nothing to show yet.</Centered>;

  if (result.status !== 'SUCCESS') {
    return (
      <Centered>
        <span className="text-danger">{result.error?.message ?? result.status.toLowerCase()}</span>
      </Centered>
    );
  }

  const form = pickForm(result);

  switch (form.kind) {
    case 'stat':
      return <StatTile label={form.label} value={form.value} masked={form.masked} />;
    case 'kpi':
      return <KpiRow items={form.items} />;
    case 'bar':
      return (
        <div className="flex h-full flex-col">
          <BarChart points={form.points} measure={form.measure} />
          {form.truncatedTo !== null && (
            <p className="mt-1 shrink-0 text-2xs text-ink-faint">
              Top {form.points.length} of {form.truncatedTo}
            </p>
          )}
        </div>
      );
    case 'line':
      return <LineChart points={form.points} measure={form.measure} />;
    case 'table':
      return <MiniTable result={result} />;
    case 'empty':
      return <Centered muted>No rows.</Centered>;
  }
}

function Centered({ children, muted }: { children: React.ReactNode; muted?: boolean }) {
  return (
    <div
      className={clsx(
        'flex h-full items-center justify-center text-center text-2xs',
        muted ? 'text-ink-faint' : 'text-ink-muted',
      )}
    >
      {children}
    </div>
  );
}
